import { FC, FormEvent, useState } from 'react';
import { Alert, Button, Input, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { devLogin } from '@/api/auth';

const { Text } = Typography;

/**
 * Username/password sign-in against POST /auth/dev-login. Rendered only when
 * VITE_DEV_LOGIN is "true"; the backend refuses it in production regardless.
 */
const DevLoginForm: FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    const result = await devLogin({ username, password });
    if (result.success) {
      // Same key as the SaaS entry path, so ProtectedRoute and the API client pick it up.
      localStorage.setItem('access_token', result.token);
      // Full navigation so useAppInit rehydrates the user from the stored token.
      window.location.assign('/dashboard');
    } else {
      const { status } = result;
      if (status === 401) {
        setError(t('auth.devLogin.invalidCredentials'));
      } else if (status === 429) {
        setError(t('auth.devLogin.tooManyAttempts'));
      } else if (status === 404) {
        setError(t('auth.devLogin.unavailable'));
      } else if (status === 503) {
        setError(t('auth.devLogin.userMissing'));
      } else {
        setError(t('auth.devLogin.failed'));
      }
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      aria-label={t('auth.devLogin.title')}
      style={{
        border: '1px dashed #fa8c16',
        borderRadius: 8,
        padding: 16,
        backgroundColor: 'var(--warning-soft)',
      }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Space size="small">
          <Text strong>{t('auth.devLogin.title')}</Text>
          <Tag color="orange">{t('auth.devLogin.badge')}</Tag>
        </Space>
        <Input
          aria-label={t('auth.devLogin.username')}
          placeholder={t('auth.devLogin.username')}
          autoComplete="username"
          value={username}
          onChange={event => setUsername(event.target.value)}
        />
        <Input.Password
          aria-label={t('auth.devLogin.password')}
          placeholder={t('auth.devLogin.password')}
          autoComplete="current-password"
          value={password}
          onChange={event => setPassword(event.target.value)}
        />
        {error && <Alert type="error" message={error} showIcon role="alert" />}
        <Button htmlType="submit" loading={submitting} block>
          {t('auth.devLogin.submit')}
        </Button>
      </Space>
    </form>
  );
};

export default DevLoginForm;
